import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, ClipboardText, ChartLine, Lock, Calendar, Sparkle } from "@phosphor-icons/react";

// Ordered by tier then by commitment so annual/monthly variants sit next to
// each other — makes the price + engagement contrast obvious at a glance.
const PLAN_OPTIONS = [
  {
    key: "base", tier: "base", name: "Standard", icon: ClipboardText, iconClass: "text-[#3A8FA5]",
    engagement: "monthly", monthlyList: "29€", monthlyEff: "29€",
    yearlyTotal: null, savings: null,
  },
  {
    key: "base_yearly", tier: "base", name: "Standard", icon: ClipboardText, iconClass: "text-[#3A8FA5]",
    engagement: "yearly", monthlyList: "29€", monthlyEff: "26,10€",
    yearlyTotal: "313,20€ / an", savings: "34,80€",
  },
  {
    key: "premium", tier: "premium", name: "Premium", icon: ChartLine, iconClass: "text-[#3A8FA5]",
    engagement: "monthly", monthlyList: "49€", monthlyEff: "49€",
    yearlyTotal: null, savings: null,
  },
  {
    key: "premium_yearly", tier: "premium", name: "Premium", icon: ChartLine, iconClass: "text-[#3A8FA5]",
    engagement: "yearly", monthlyList: "49€", monthlyEff: "44,10€",
    yearlyTotal: "529,20€ / an", savings: "58,80€",
  },
];

export default function ChoosePlanDialog({ open, onOpenChange, currentPlan, commitmentLock, onSelect }) {
  const pick = (target) => {
    onOpenChange(false);
    // Small delay so this dialog fully unmounts before ChangePlanDialog opens —
    // avoids Radix focus-trap glitches when two dialogs overlap.
    setTimeout(() => onSelect?.(target), 60);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="choose-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1E3A5F]">
            Changer d'abonnement
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5F758F]">
            Sélectionnez la formule vers laquelle vous souhaitez basculer. L'étape suivante affichera l'ajustement de prix au prorata avant confirmation.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          {PLAN_OPTIONS.map((opt) => {
            const isCurrent = opt.key === currentPlan;
            const locked = !isCurrent && (commitmentLock?.(opt.key) || "");
            const disabled = isCurrent || !!locked;
            const Icon = opt.icon;
            const isYearly = opt.engagement === "yearly";
            const wrapperTone = isCurrent
              ? "border-[#3A8FA5] bg-[#E6F1F5] cursor-default"
              : locked
              ? "border-[#E1E9F0] bg-[#F4F7FB] opacity-70 cursor-not-allowed"
              : isYearly
              ? "border-[#3A8FA5]/40 bg-white hover:border-[#3A8FA5] hover:shadow-[0_2px_10px_rgba(58,143,165,0.12)] ring-1 ring-[#3A8FA5]/10"
              : "border-[#E1E9F0] bg-white hover:border-[#1E3A5F] hover:shadow-[0_2px_8px_rgba(30,58,95,0.08)]";
            return (
              <button
                key={opt.key}
                type="button"
                disabled={disabled}
                onClick={() => pick(opt.key)}
                title={locked || undefined}
                className={`relative text-left border-2 rounded-[14px] p-4 flex flex-col transition-all ${wrapperTone}`}
                data-testid={`choose-plan-${opt.key}`}
              >
                {/* Engagement pill — brightest visual signal */}
                <div className="flex items-center gap-2 mb-3 min-h-[22px]">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                      isYearly
                        ? "bg-[#3A8FA5] text-white"
                        : "bg-[#F4F7FB] text-[#5F758F] border border-[#E1E9F0]"
                    }`}
                    data-testid={`choose-plan-${opt.key}-badge`}
                  >
                    {isYearly ? (
                      <>
                        <Calendar size={11} weight="fill" />
                        Engagement annuel · −10%
                      </>
                    ) : (
                      "Sans engagement"
                    )}
                  </span>
                  {isCurrent && (
                    <span className="ml-auto text-[10px] font-bold uppercase tracking-widest text-[#3A8FA5]">
                      Actuel
                    </span>
                  )}
                  {locked && !isCurrent && (
                    <Lock size={14} weight="duotone" className="ml-auto text-[#5F758F]" />
                  )}
                </div>

                {/* Plan name */}
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={22} weight="duotone" className={opt.iconClass} />
                  <span className="text-xl font-bold tracking-tight text-[#1E3A5F]">{opt.name}</span>
                </div>

                {/* Price row — same baseline across all 4 cards. */}
                <div className="flex items-baseline gap-2 mb-1 min-h-[36px]">
                  {isYearly && (
                    <span className="text-base line-through text-[#8FA1B8] tabular-nums font-medium">
                      {opt.monthlyList}
                    </span>
                  )}
                  <span className="text-3xl font-bold text-[#1E3A5F] tabular-nums leading-none">
                    {opt.monthlyEff}
                  </span>
                  <span className="text-xs text-[#5F758F] font-medium">/ mois TTC</span>
                </div>

                {/* Reserved details block — always 2 lines tall so every card
                    aligns vertically regardless of engagement type. */}
                <div className="mt-1.5 space-y-0.5 min-h-[36px]">
                  {isYearly ? (
                    <>
                      <p className="text-[13px] text-[#1E3A5F] font-semibold tabular-nums">
                        {opt.yearlyTotal}
                      </p>
                      <p className="text-[11px] text-[#3A8FA5] font-bold flex items-center gap-1">
                        <Sparkle size={11} weight="fill" />
                        Vous économisez {opt.savings} / an
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-[13px] text-[#1E3A5F] font-semibold tabular-nums invisible">.</p>
                      <p className="text-[11px] text-[#5F758F]">
                        Facturation mensuelle · résiliation à tout moment
                      </p>
                    </>
                  )}
                </div>

                {/* Locked reason (no vertical reservation — appears only when
                    the plan is unreachable so the extra height stays local). */}
                {locked && !isCurrent && (
                  <p className="text-[11px] text-[#8A3E20] mt-3 leading-relaxed bg-[#FBE8E1] border border-[#F0B686] rounded-md px-2 py-1.5">
                    {locked}
                  </p>
                )}

                {/* Footer — reserved on every card so heights match. */}
                <div className="mt-auto pt-3 border-t border-[#E1E9F0] flex items-center gap-1.5 text-xs font-bold min-h-[36px]">
                  {isCurrent ? (
                    <span className="text-[#3A8FA5] flex items-center gap-1.5">
                      <CheckCircle size={14} weight="fill" /> Formule actuelle
                    </span>
                  ) : locked ? (
                    <span className="text-[#5F758F] flex items-center gap-1.5">
                      <Lock size={14} weight="duotone" /> Indisponible
                    </span>
                  ) : (
                    <>
                      <CheckCircle size={14} weight="duotone" className="text-[#3A8FA5]" />
                      <span className="text-[#3A8FA5]">Choisir cette formule</span>
                      <ArrowRight size={14} weight="bold" className="ml-auto text-[#3A8FA5]" />
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex justify-end pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-[10px] font-semibold"
            data-testid="choose-plan-cancel"
          >
            Annuler
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
